"use client"

import { Images } from "@/constants/images"
import { useStickySidebar } from "@/hook/useHomepage"
import { Link, usePathname } from "@/i18n/routing"
import { t } from "@/languages/i18n"
import { BottomNavProps } from "@/types/types"
import Image from "next/image"
import React, { useRef } from "react"

const BottomNav: React.FC<BottomNavProps> = ({ locale, onMenuClick, socialLinks }) => {
  const pathname = usePathname()
  const isHome = pathname === "/" || pathname === ""
  const isHot = pathname?.startsWith("/tin-nong")
  const isVideo = pathname?.startsWith("/video")
  const sidebarRef = useRef<HTMLDivElement>(null)
  const isSticky = useStickySidebar(sidebarRef)

  const navItemClass = (isActive: boolean) =>
    `flex flex-col items-center justify-center hover:opacity-80 hover:duration-400 transition-colors max-w-fit ${
      isActive ? "font-bold" : "font-medium"
    }`

  const navItemTextClass = (isActive: boolean) =>
    `text-[0.75rem] leading-[24.8px] min-[1180px]:text-base min-[1180px]:leading-[24.8px] leading-[18.59px] text-[#333] hover:text-[#1EAD46] transition-all duration-400 ${
      isActive ? "font-bold" : "font-normal"
    }`

  const Separator = () => <div className="w-[70.39px] h-px border-t border-[#CED4DA]" />

  const renderNavItem = (
    href: string,
    isActive: boolean,
    icon: string,
    alt: string,
    label: string,
    isMobile: boolean = false,
    isFirst: boolean = false,
  ) => {
    const baseClass = isMobile
      ? `${navItemClass(isActive)} flex-1 h-full`
      : `${navItemClass(isActive)} w-full min-w-[70px] ${isFirst ? "pb-4" : "pt-5 pb-4"}`
    const textClass = isMobile ? navItemTextClass(isActive) : `${navItemTextClass(isActive)} text-center block`
    const iconSize = isMobile ? 24 : 32

    return (
      <Link href={href} className={baseClass}>
        <Image
          src={icon}
          alt={alt}
          width={iconSize}
          height={iconSize}
          className={`w-8 h-8 ${isMobile ? "" : "mx-auto"}`}
        />
        <span className={`${textClass} lowercase first-letter:uppercase`}>{label}</span>
      </Link>
    )
  }

  const renderSocialItems = (isMobile: boolean = false) => {
    const items: Array<{ key: string; href: string; icon: string; label: string }> = []
    if (socialLinks?.facebook) {
      items.push({
        key: "facebook",
        href: socialLinks.facebook,
        icon: Images.facebookSidebarIcon.src,
        label: t("Facebook"),
      })
    }
    if (socialLinks?.youtube) {
      items.push({
        key: "youtube",
        href: socialLinks.youtube,
        icon: Images.youtubeSidebarIcon.src,
        label: t("Youtube"),
      })
    }

    return items.map(item => {
      const baseClass = isMobile
        ? `${navItemClass(false)} flex-1 h-full`
        : `${navItemClass(false)} w-full min-w-[70px] pt-5 pb-4`
      const textClass = isMobile ? navItemTextClass(false) : `${navItemTextClass(false)} text-center block`
      const iconSize = isMobile ? 24 : 32

      return (
        <Link key={item.key} href={item.href} target="_blank" rel="noopener noreferrer" className={baseClass}>
          <Image
            src={item.icon}
            alt={item.label}
            width={iconSize}
            height={iconSize}
            className={`w-8 h-8 ${isMobile ? "" : "mx-auto"}`}
          />
          <span className={`${textClass} lowercase first-letter:uppercase`}>{item.label}</span>
        </Link>
      )
    })
  }

  return (
    <>
      {/* Mobile: Bottom Navigation */}
      <div className="min-[1180px]:hidden fixed bottom-0 left-0 right-0 bg-white z-50">
        <div className="flex items-center justify-around h-[72px]">
          {renderNavItem(
            "/",
            isHome,
            isHome ? Images.homepageRedIcon : Images.homepageGrayIcon,
            "Homepage",
            t("HOMEPAGE"),
            true,
          )}
          {renderNavItem(
            "/category/tin-nong",
            isHot,
            isHot ? Images.hotNewsRedIcon : Images.hotNewsGrayIcon,
            "Hot News",
            t("Nóng"),
            true,
          )}
          {renderNavItem(
            "/video",
            isVideo,
            isVideo ? Images.videoRedIcon : Images.videoGrayIcon,
            "Video",
            t("Video"),
            true,
          )}
          {renderSocialItems(true)}
          <button onClick={onMenuClick} className={`${navItemClass(false)} flex-1 h-full`}>
            <Image src={Images.menuIcon} alt="Menu" width={24} height={24} className="w-8 h-8" />
            <span className={`${navItemTextClass(false)} lowercase first-letter:uppercase`}>{t("Search")}</span>
          </button>
        </div>
      </div>

      {/* Desktop: Left Sidebar */}
      <div
        ref={sidebarRef}
        className={`hidden min-[1399px]:flex z-50 ${
          isSticky ? "fixed top-[48px]" : "absolute"
        } bg-transparent w-[120px]`}
        style={{
          left: `calc((100vw - 1160px) / 2 - 120px)`,
          transform: "translateZ(0)",
          willChange: "transform",
          backfaceVisibility: "hidden",
        }}
      >
        <div className={`flex flex-col items-center px-4 w-full ${isSticky ? "py-4" : "pt-16"}`}>
          {renderNavItem(
            "/",
            isHome,
            isHome ? Images.homepageRedIcon : Images.homepageGrayIcon,
            "Homepage",
            t("Homepage"),
            false,
            true,
          )}
          <Separator />
          {renderNavItem(
            "/category/tin-nong",
            isHot,
            isHot ? Images.hotNewsRedIcon : Images.hotNewsGrayIcon,
            "Hot News",
            t("Tin nóng"),
          )}
          <Separator />
          {renderNavItem("/video", isVideo, isVideo ? Images.videoRedIcon : Images.videoGrayIcon, "Video", t("Video"))}
          {(() => {
            const socialItems = renderSocialItems()
            return (
              <>
                {socialItems.length > 0 && <Separator />}
                {socialItems.map((item, index) => (
                  <React.Fragment key={item.key}>
                    {index > 0 && <Separator />}
                    {item}
                  </React.Fragment>
                ))}
              </>
            )
          })()}
        </div>
      </div>
    </>
  )
}

export default BottomNav
